import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Switch } from "../components/ui/switch";

export default function UsersList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([api.get("/users"), api.get("/stores")]);
      setUsers(u.data);
      const map = {};
      s.data.forEach((x) => (map[x.id] = x.name));
      setStores(map);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const remove = async (u) => {
    if (!confirm(t("users.deleteConfirm", { username: u.username }))) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(t("users.deleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const renderStoreCell = (u) => {
    if (u.group === "store") {
      return u.store_id ? stores[u.store_id] || "—" : "—";
    }
    if (u.group === "superstore") {
      const ids = u.store_ids || [];
      if (ids.length === 0) return "—";
      if (ids.length <= 2) {
        return ids.map((id) => stores[id] || "—").join(", ");
      }
      return (
        <span title={ids.map((id) => stores[id] || "—").join(", ")}>
          {t("users.storesAssigned", { count: ids.length })}
        </span>
      );
    }
    return "—";
  };

  const groupPillClass = (g) => {
    if (g === "admin") return "pill pill-info";
    if (g === "superstore") return "pill pill-warning";
    return "pill pill-success";
  };

  return (
    <div data-testid="users-list-page">
      <div className="flex items-end justify-between mb-7 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-slate-400 mb-1">
            {t("users.breadcrumb")}
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900">{t("users.title")}</h1>
        </div>
        <Link
          to="/users/new"
          className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold text-sm flex items-center gap-2"
          data-testid="add-user-btn"
        >
          <Plus size={16} /> {t("users.addUser")}
        </Link>
      </div>

      <div className="pg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50/70">
              <th className="px-5 py-3 font-semibold">{t("users.columns.name")}</th>
              <th className="px-5 py-3 font-semibold">{t("users.columns.username")}</th>
              <th className="px-5 py-3 font-semibold">{t("users.columns.email")}</th>
              <th className="px-5 py-3 font-semibold">{t("users.columns.group")}</th>
              <th className="px-5 py-3 font-semibold">{t("users.columns.store")}</th>
              <th className="px-5 py-3 font-semibold">{t("users.columns.active")}</th>
              <th className="px-5 py-3 font-semibold text-right">{t("users.columns.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-400">{t("common.loading")}</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-400">{t("users.noUsers")}</td></tr>
            ) : (
              users.map((u) => (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`user-row-${u.id}`}>
                  <td className="px-5 py-3 font-medium text-slate-800">{u.name}</td>
                  <td className="px-5 py-3 mono text-slate-700">{u.username}</td>
                  <td className="px-5 py-3 text-slate-600">{u.email || "—"}</td>
                  <td className="px-5 py-3">
                    <span className={groupPillClass(u.group)}>
                      {t(`groups.${u.group}`, { defaultValue: u.group })}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-slate-600">{renderStoreCell(u)}</td>
                  <td className="px-5 py-3">
                    <Switch checked={u.active} onCheckedChange={() => toggle(u)} data-testid={`toggle-user-${u.id}`} />
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => navigate(`/users/${u.id}/edit`)} className="p-2 rounded-md text-slate-500 hover:bg-blue-50 hover:text-blue-600" title={t("common.edit")} aria-label={t("common.edit")} data-testid={`edit-user-${u.id}`}>
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => remove(u)} className="p-2 rounded-md text-slate-500 hover:bg-rose-50 hover:text-rose-600" title={t("common.delete")} aria-label={t("common.delete")} data-testid={`delete-user-${u.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
